import { HeroBannerSlide as HeroBannerSlideType, SanityImage } from '@workspace/interfaces';
import { HERO_ASPECT, sanityHeroLoader, sanityImageSrc } from '@/sanity/lib/image';
import { stegaClean } from '@sanity/client/stega';
import { cn } from '@workspace/lib';
import { AppImage } from '@workspace/ui/common';
import { HeroBannerVideo } from './HeroBannerVideo';
import { HeroBannerSize } from '../hero-banner.types';
import { getHeroBannerMinHeightClass } from '../hero-banner-util';

type HeroBannerImage = HeroBannerSlideType['image'] | SanityImage;

interface HeroBannerBackgroundProps {
  blockImage?: HeroBannerImage;
  contentPosition: 'left' | 'center' | 'right';
  isVideo?: boolean | null;
  videoUrl?: string | null;
  videoFile?: SanityImage | null;
  poster?: SanityImage | null;
  isActive?: boolean | null;
  size?: HeroBannerSize;
  shouldPreload?: boolean;
  showGradient?: boolean | null;
}

const getGradientClass = (contentPosition: 'left' | 'center' | 'right'): string => {
  switch (contentPosition) {
    case 'center':
      return 'hero-gradient-center';
    case 'right':
      return 'hero-gradient-right';
    default:
      return 'hero-gradient-left';
  }
};

export function HeroBannerBackground({
  blockImage,
  contentPosition,
  isVideo = false,
  videoUrl,
  videoFile,
  poster,
  isActive = false,
  shouldPreload = false,
  size = 'medium',
  showGradient = true,
}: HeroBannerBackgroundProps) {
  const gradientClass = getGradientClass(contentPosition);
  const minHeightClass = getHeroBannerMinHeightClass(size);
  const shouldPrioritize = shouldPreload || (isActive ?? false);

  // Render video background if isVideo is true and videoUrl exists
  if (isVideo && (videoUrl || videoFile)) {
    return (
      <div className={cn('absolute inset-0', minHeightClass)} suppressHydrationWarning>
        <HeroBannerVideo
          videoUrl={videoUrl ?? ''}
          videoFile={videoFile ?? null}
          poster={poster ?? null}
          isActive={isActive}
          shouldPreload={shouldPrioritize}
        />
        {showGradient !== false && (
          <div className={cn('absolute inset-0 pointer-events-none hero-gradient', gradientClass)} />
        )}
      </div>
    );
  }

  // Render image background if blockImage exists
  if (blockImage?.url) {
    return (
      <div className={cn('absolute inset-0', minHeightClass)} suppressHydrationWarning>
        <AppImage
          loader={sanityHeroLoader(blockImage, HERO_ASPECT)}
          src={sanityImageSrc(blockImage)}
          alt={stegaClean(blockImage?.alt) || 'Hero Banner Image'}
          fill
          sanityImage={blockImage}
          priority={shouldPrioritize}
          fetchPriority={shouldPrioritize ? 'high' : undefined}
          sizes="
          (min-width:1536px) 1536px,
          (min-width:1280px) 1280px,
          (min-width:1024px) 1024px,
          (min-width:768px)   768px,
          (min-width:640px)   640px,
          100vw"
        />
        {showGradient !== false && (
          <div className={cn('absolute inset-0 pointer-events-none hero-gradient', gradientClass)} />
        )}
      </div>
    );
  }

  // Render gradient only if no image or video
  return (
    <div
      className={cn('absolute inset-0 pointer-events-none hero-gradient', gradientClass, minHeightClass)}
      suppressHydrationWarning
    />
  );
}
